import { useState, useEffect } from 'react';

import { BREAKPOINT_XLARGE } from '../styles/common/breakpoints';

interface WindowSize {
  isTouchDevice: boolean;
  width: number;
  height: number;
  isGreaterThanXLarge: boolean
}

function getWindowDimensions(): WindowSize {
  const {
    innerWidth, innerHeight, outerHeight, outerWidth,
  } = window;

  /**
   * For an unknown reason, window.innerWidth and window.innerHeight are reporting larger values that
   * expected compared to the device's actual dimensions when rotating a device.
   * However, in the cases where the inner values are wrong, the outerWidth and outerHeight values
   * match the expected resolution.
   * We use Math.min to get the lower of the two values to allow the island positional calculations
   * to use the expected dimensions (which should be the inner values).
   */
  const width = Math.min(innerWidth, outerWidth);
  const height = Math.min(innerHeight, outerHeight);

  return {
    isTouchDevice: ('ontouchstart' in document.documentElement),
    width,
    height,
    isGreaterThanXLarge: width >= BREAKPOINT_XLARGE,
  };
}

function useWindowSize(initTimeout = 0): WindowSize {
  const originalWindowSize = getWindowDimensions();
  const [windowSize, setWindowSize] = useState<WindowSize>(originalWindowSize);

  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      const resizeNow = () => setWindowSize(getWindowDimensions());

      if (!initTimeout) { resizeNow(); } else { setTimeout(resizeNow, initTimeout); }

      // Upon device rotation window.innerHeight/innerWidth aren't yet updated fully
      // so an arbitrary few ms pause is neccesary
      setTimeout(resizeNow, 1000);
    }

    window.addEventListener('resize', handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, [originalWindowSize, windowSize]);
  return windowSize;
}

export default useWindowSize;
